import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { createNormalPidResponse, isSupportedNormalPidResponseMsgType, isSupportedPidResponseMsgType, } from "@poscon/shared-types";
import { getDownlinkMessageCategory, EramCpdlcSession, formatCpdlcMessage } from "@poscon/eram-cpdlc";
import { EramPrompt } from "./EramPrompt";
import { eramFontNameMap, eramTextDimensionMap, getBitmapTextStyles } from "../../eramFont";
import { MenuElement } from "./MenuElement";
import { createItemOption, ViewItemOptionContainer } from "./ViewItemOptionContainer";
import { useRootSelector } from "../stateSelectors";
import { closeSpecificViewOption, selectedViewOptionSelector, toggleSelectedViewOption, } from "../slices/commonSlice";
import { useDispatch } from "react-redux";
import { colorNameMap } from "../../colorNameMap";
import { baseEramHubConnection } from "../../eramHubConnectionFactory";
import { sectorIdSelector } from "../slices/connectionSlice";
import { useDispatchCommand } from "../contexts/commonContext";
export const cpdlcPidView = "CPDLC_PID_MENU";
const fontFamily = eramFontNameMap[2];
const fontDim = eramTextDimensionMap[2];
const minWidth = fontDim.width * 9;
const tint = colorNameMap.lightGrey;
function createUnableOptions(fp, dialog, isSupported = true) {
    const options = [];
    options.push(createItemOption("UNA", () => baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA")), createItemOption("UNA/RES", () => baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA/RES"), {
        disabled: !isSupported,
    }), createItemOption("UNA/TFC", () => baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA/TFC"), {
        disabled: !isSupported,
    }), createItemOption("UNA/WX", () => baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA/WX"), {
        disabled: !isSupported,
    }));
    return options;
}
function getPidItemOptions(fp, dialog, dispatchCommand, deselect) {
    const options = [];
    const msgType = dialog.messages[0].elements[0].type;
    const msgCategory = getDownlinkMessageCategory(msgType);
    if (msgCategory === "normalDownlink") {
        const uplinkCommand = createNormalPidResponse(fp.callsign, dialog.messages[0]);
        const isSupported = isSupportedNormalPidResponseMsgType(msgType) || isSupportedPidResponseMsgType(msgType);
        // TODO: implement "WHEN ABLE PROCEED DIRECT" prompt for deviation requests
        options.push(createItemOption("UPLINK", () => {
            if (uplinkCommand) {
                dispatchCommand(uplinkCommand);
            }
            baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UPLINK");
            deselect();
        }, { disabled: !isSupported }));
        if (!dialog.messages.some((m) => m.elements[0].type === "UM1")) {
            options.push(createItemOption("STANDBY", () => {
                baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "STANDBY");
                deselect();
            }, { disabled: !isSupported }));
        }
        options.push(...createUnableOptions(fp, dialog, isSupported));
    }
    else if (msgCategory === "reportDownlink") {
        options.push(createItemOption("DELETE", () => {
            baseEramHubConnection.deleteCdplcDialog(fp.id, dialog.id);
            deselect();
        }));
    }
    else if (msgCategory === "voiceContactRequest") {
        options.push(createItemOption("ROGER", () => {
            baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "ROGER");
            deselect();
        }), createItemOption("UNA", () => {
            baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA");
            deselect();
        }));
    }
    else {
        // TODO: add options for other dialog types
        options.push(createItemOption("UNA", () => {
            baseEramHubConnection.sendPidDialogResponse(fp.id, dialog.id, "UNA");
            deselect();
        }));
    }
    return options;
}
export const PidRow = ({ fp, dialog, y }) => {
    const dispatch = useDispatch();
    const dispatchCommand = useDispatchCommand();
    const ref = useRef(null);
    const selectedItem = useRootSelector(selectedViewOptionSelector);
    const path = `${cpdlcPidView}/row/${fp.id}`;
    const descr = formatCpdlcMessage(dialog?.messages[0]);
    const description = descr?.description ?? "";
    const isStandby = dialog.messages.some((m) => m.elements[0].type === "UM1");
    const optionsOffset = description.length * fontDim.width + 5 + (isStandby ? fontDim.width * 4 + 1 : 0);
    const options = getPidItemOptions(fp, dialog, dispatchCommand, () => dispatch(closeSpecificViewOption(path)));
    // TODO: display star for due to reason if available and add due to reason to the popup menu
    return (_jsxs("container", { ref: ref, y: y, eventMode: "static", zIndex: selectedItem?.startsWith(path) ? 5 : 4, children: [isStandby && (_jsx("bitmapText", { x: 4, y: 2, text: "SBY-", style: { ...getBitmapTextStyles(fontFamily), fill: tint } })), _jsx(MenuElement, { x: isStandby ? fontDim.width * 4 + 4 : 4, text: description, tint: tint, borderColor: selectedItem === path ? tint : 0, disabled: description.length === 0, selected: selectedItem === path, fillColor: 0, selectReverseVideo: true, onmousedown: () => dispatch(toggleSelectedViewOption(path)) }), selectedItem?.startsWith(path) && options && (_jsx(ViewItemOptionContainer, { baseX: 4, xOffset: optionsOffset, options: options, maxRowWidth: fontDim.width * 30 + 4 }))] }));
};
export const CpdlcPidMenu = ({ fp, session, ref, ...promptProps }) => {
    const sectorId = useRootSelector(sectorIdSelector);
    const dialogs = EramCpdlcSession.getSectorPidDialogs(session, sectorId);
    const dialogsHeight = dialogs.length * (fontDim.height + 4) + 5;
    const showView = dialogs.length > 0;
    useEffect(() => {
        if (!showView) {
            promptProps.closeMenu();
        }
    }, [showView, promptProps.closeMenu, promptProps]);
    const width = Math.max(minWidth, ...dialogs.map((dialog) => (formatCpdlcMessage(dialog.messages[0]).description.length +
        (dialog.messages.some((m) => m.elements[0].type === "UM1") ? 4 : 0)) *
        fontDim.width +
        12));
    let y = 4;
    return (_jsx(EramPrompt, { ref: ref, title: fp.callsign, width: width, height: dialogsHeight, showX: true, ...promptProps, children: dialogs.map((dialog) => {
            const _y = y;
            y += fontDim.height + 4;
            return _jsx(PidRow, { fp: fp, dialog: dialog, y: _y }, dialog.id);
        }) }));
};
//# sourceMappingURL=CpdlcPidMenu.js.map